/**
 * Fotos am Einsatz — `PWA-004`.
 *
 * `07` §7.12: „Fotos vom Monteur werden zunächst dem Einsatz zugeordnet und
 * anschließend nach Nextcloud übertragen." Diese Reihenfolge ist der Grund,
 * aus dem ein Foto hier zuerst nur ein Eintrag in der Outbox ist: Es gehört
 * dem Einsatz, bevor es irgendwo liegt.
 *
 * **Keine automatische Doppelablage.** Derselbe Abschnitt: Soll ein Foto
 * rechtssicher archiviert werden, erzeugt ein definierter Workflow zusätzlich
 * ein Paperless-Dokument — „keine automatische Doppelablage ohne Regel". Die
 * App bietet deshalb nichts an, was ein Foto in zwei Systeme legt.
 *
 * **Was hier fehlt, ist die Kamera.** Der echte Gerätezugriff braucht eine
 * gesicherte Herkunft und einen Ort für die Binärdaten; beides gehört zu
 * `PWA-004` mit dem verschlüsselten Cache aus `05` §5.6. Was diese Ansicht
 * heute leistet, ist der Ablauf drumherum — dass ein Foto am Einsatz hängt,
 * in der Warteschlange sichtbar ist und im Bericht auftaucht.
 */

import { useState } from "react";

import type { Einsatz } from "@hwc/demo-data";

import { useMonteur } from "../../src/Sitzung.js";
import { uhrzeit } from "../../src/bausteine.js";

export function Fotos({ einsatz }: { einsatz: Einsatz }) {
  const { auflage, zustandVon, nimmFoto } = useMonteur();
  const eigene = auflage(einsatz).fotos;
  const zustand = zustandVon(einsatz);
  const gesperrt = zustand === "geprueft" || zustand === "storniert";

  const [zaehler, setzeZaehler] = useState(1);

  return (
    <>
      <h2>Fotos</h2>

      {eigene.length === 0 ? (
        <p className="leer leise">Noch keine Fotos aufgenommen.</p>
      ) : (
        <ul className="postenliste">
          {eigene.map((f) => (
            <li key={f.id} className="postenliste__wartend">
              <span>
                {f.name}
                <span className="abzeichen abzeichen--warnung">wartet auf Upload</span>
              </span>
              <span>{uhrzeit(f.aufgenommen)}</span>
            </li>
          ))}
        </ul>
      )}

      {!gesperrt && (
        <div className="knopfleiste">
          <button
            type="button"
            className="knopfleiste__knopf"
            onClick={() => {
              const nummer = String(zaehler).padStart(2, "0");
              nimmFoto(einsatz, `Foto ${nummer} — ${einsatz.titel.slice(0, 30)}.jpg`);
              setzeZaehler((z) => z + 1);
            }}
          >
            Foto aufnehmen
          </button>
        </div>
      )}

      <p className="leise fussnote">
        Der Kamerazugriff entsteht mit <code>PWA-004</code> und dem verschlüsselten
        Cache aus <code>05</code> §5.6. Bis dahin legt dieser Knopf einen Eintrag an, der
        den Weg durch die Warteschlange nimmt — genau den, den ein echtes Foto später
        nimmt.
      </p>
    </>
  );
}
